import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import SegmentedToggle from "../ui/segmented-toggle";
import {
  Sparkles,
  Loader2,
  Check,
  Trash2,
  Clock,
  ChevronDown,
  Copy,
} from "lucide-react";
import { cn } from "lib/utils";
import {
  getBadgeClass,
  getDotClass,
  SET_SCHEME_DIMENSIONS,
} from "../../utils/planVisuals";
import { seedSetCells, resolveSetCells } from "../../utils/setSchemeEditing";
import { isCountedUnit, isTimedUnit } from "../../utils/planNodeUtils";
import useRepEstimate from "../../hooks/useRepEstimate";
import StepAmountFields, { LOAD_UNITS, UNIT_LABELS, startValueFor } from "./StepAmountFields";
import llmApi from "../../apis/llmApi";

// Two families, and the picker says so. Only Group/Circuit hold children and
// take a rounds/sets count (or a time cap) — everything in "Steps" is a leaf
// that runs. Warmup/Strength/Cardio/HIIT/Mobility/Cooldown are themed flavours
// of Work: same runtime behaviour, different colour, icon and unit list. That
// distinction is the whole reason a "Strength" row can't hold exercises or run
// for rounds, and a flat A–Z list gave no hint of it.
const TYPE_GROUPS = [
  { label: "Blocks", holdsChildren: true, types: ["Group", "Circuit"] },
  {
    label: "Steps",
    holdsChildren: false,
    types: [
      "Work",
      "Rest",
      "Transition",
      "Warmup",
      "Cooldown",
      "Strength",
      "Cardio",
      "HIIT",
      "Mobility",
    ],
  },
];


// Per-type valid units. Driven by docs/schema.md authoring rules:
//   • Rest/Transition: clock time only (no rep-based recovery).
//   • Work-like leaves: time or a counted target (reps / calories).
//   • Group/Circuit containers: a round/set count, or a time unit for a
//     fixed-time cap (AMRAP / "for time" — children become a checklist run for
//     the whole cap rather than a loop; see docs/schema.md).
//
// "cal" is a calorie target on an ergo machine ("100 cal Assault Bike") — the
// machine's own display is the referee, so it plays exactly like reps: a
// self-paced phase that advances on the Next press, with an estimated duration
// (see estimatePerCalSec) standing in for the projection. Offered only on the
// types that plausibly name a machine; a calorie target on a Mobility drill is
// a mis-set unit, not a prescription.
const UNITS_BY_TYPE = {
  Rest: ["s", "m"],
  Transition: ["s", "m"],
  Work: ["s", "m", "reps", "cal"],
  Warmup: ["s", "m", "reps", "cal"],
  Cooldown: ["s", "m", "reps", "cal"],
  Strength: ["s", "m", "reps"],
  Cardio: ["s", "m", "reps", "cal"],
  HIIT: ["s", "m", "reps", "cal"],
  Mobility: ["s", "m", "reps"],
  Group: ["rounds", "sets", "s", "m"],
  Circuit: ["rounds", "sets", "s", "m"],
};
const DEFAULT_UNITS = ["s", "m", "reps"];
const validUnitsFor = (t) => UNITS_BY_TYPE[t] || DEFAULT_UNITS;

// Container types hold children. Creating one yields an empty group the plan
// view renders with an "Add step" affordance (issue #44).
const CONTAINER_TYPES = TYPE_GROUPS.find((g) => g.holdsChildren).types;

// Recovery leaves carry no load, effort or per-set variation — docs/schema.md
// scopes all three to Work-like nodes.
const RECOVERY_TYPES = ["Rest", "Transition"];

// The two ways to prescribe load. They are mutually exclusive by schema:
// `intensityPct` takes precedence over `weight` when both are present, so
// offering both as separate inputs invites setting one that silently does
// nothing. One control, one stored field (issue #69).
const loadDimFor = (loadUnit) =>
  loadUnit === "pct" ? "intensityPct" : "weight";

const FIELD_LABEL_CLASS =
  "text-[10px] text-muted-foreground uppercase tracking-widest font-semibold";

/**
 * One dimension of the per-set editor — a numbered cell per set, so the ramp
 * lines up with the sets it prescribes. A blank cell falls back to the static
 * field on save (see resolveSetCells), so a partly-filled row can never write a
 * hole into the plan.
 */
const SetCellRow = ({ label, cells, onChange }) => (
  <div className="space-y-1">
    <Label className={FIELD_LABEL_CLASS}>{label}</Label>
    <div className="flex flex-wrap gap-1.5">
      {cells.map((cell, idx) => (
        <div key={idx} className="flex flex-col items-center gap-0.5">
          <span className="text-[9px] text-muted-foreground tabular-nums">
            {idx + 1}
          </span>
          <Input
            type="number"
            value={cell}
            onChange={(e) => onChange(idx, e.target.value)}
            aria-label={`${label}, set ${idx + 1}`}
            className="h-9 w-14 px-1 text-center text-sm"
          />
        </div>
      ))}
    </div>
  </div>
);

// Format the structured AI response (description + form tips + common
// mistakes + alternatives) into a single plain-text block that lives on
// node.description. Keeps the coach view readable too — it just renders
// the field as-is.
const formatAiResponse = (res) => {
  if (!res) return "";
  const parts = [];
  if (res.description) parts.push(res.description.trim());
  if (res.formTips?.length) {
    parts.push(
      "Form tips:\n" + res.formTips.map((t) => `• ${t}`).join("\n"),
    );
  }
  if (res.commonMistakes?.length) {
    parts.push(
      "Common mistakes:\n" + res.commonMistakes.map((m) => `• ${m}`).join("\n"),
    );
  }
  if (res.alternatives?.length) {
    parts.push(
      "Alternatives:\n" + res.alternatives.map((a) => `• ${a}`).join("\n"),
    );
  }
  return parts.join("\n\n");
};

const ExerciseDetailDialog = ({
  open,
  onOpenChange,
  node,
  indexPath,
  hasChildren,
  // How many sets the enclosing Group runs this node for. 1 (or absent) means
  // it does not repeat, and the per-set editor stays hidden.
  setCount = 1,
  mode = "edit",
  onSave,
  onCreate,
  onDelete,
  onDuplicate,
  llmSettings,
  userProfile,
}) => {
  const isCreate = mode === "create";
  const [label, setLabel] = useState("");
  const [type, setType] = useState("Work");
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("s");
  const [description, setDescription] = useState("");
  const [load, setLoad] = useState("");
  const [loadUnit, setLoadUnit] = useState("kg");
  const [intensityTarget, setIntensityTarget] = useState("");
  const [autoStart, setAutoStart] = useState(false);
  const [linkToPrevious, setLinkToPrevious] = useState(false);
  const [perSet, setPerSet] = useState(false);
  const [valueCells, setValueCells] = useState([]);
  const [loadCells, setLoadCells] = useState([]);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const abortRef = useRef(null);
  const titleInputRef = useRef(null);

  useEffect(() => {
    if (open && node) {
      setLabel(node.label || node.exercise_name || "");
      setType(node.type || "Work");
      setValue(node.value ?? node.duration ?? node.reps ?? "");
      // A unitless Group is a repeat count at runtime (translatePlan), so it
      // must not open as a seconds cap and save back as an AMRAP.
      setUnit(node.unit || validUnitsFor(node.type || "Work")[0]);
      setDescription(node.description || "");

      // Seed the load control from whichever dimension the node actually uses.
      // `intensityPct` wins when both are set, because that is the one the rep
      // estimator resolves against (docs/schema.md, VBT Load Resolution).
      const pct = Number(node.intensityPct);
      const kg = Number(node.weight);
      const usePct = Number.isFinite(pct) && pct > 0;
      const staticLoad = usePct ? pct : Number.isFinite(kg) && kg > 0 ? kg : null;
      setLoadUnit(usePct ? "pct" : "kg");
      setLoad(staticLoad === null ? "" : String(staticLoad));
      setIntensityTarget(node.intensityTarget || "");
      setAutoStart(node.autoStart === true);
      setLinkToPrevious(node.linkToPrevious === true);

      const scheme =
        node.setScheme && typeof node.setScheme === "object"
          ? node.setScheme
          : null;
      const cellCount = Number(setCount);
      setValueCells(seedSetCells(scheme?.value, node.value, cellCount));
      setLoadCells(
        seedSetCells(
          scheme?.[loadDimFor(usePct ? "pct" : "kg")],
          staticLoad,
          cellCount,
        ),
      );
      setPerSet(
        !!scheme &&
          SET_SCHEME_DIMENSIONS.some(
            (dim) => Array.isArray(scheme[dim]) && scheme[dim].length > 0,
          ),
      );

      setAiLoading(false);
      setAiError(null);
    }
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [open, node, setCount]);

  // Focus + select the title on open, but only when the label is a placeholder
  // — editing an existing exercise should keep its cursor behaviour. rAF
  // defers past Radix's own focus-trap pass so .select() wins.
  useEffect(() => {
    if (!open) return undefined;
    const raf = requestAnimationFrame(() => {
      const el = titleInputRef.current;
      if (!el) return;
      el.focus();
      const currentLabel = node?.label || node?.exercise_name || "";
      if (!currentLabel || currentLabel === "New exercise") {
        el.select();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [open, node]);

  // Container-typed nodes are never "leaves" even while childless — hides
  // the per-exercise AI notes button and rep estimate for groups.
  const isLeaf = !hasChildren && !CONTAINER_TYPES.includes(type);
  const validUnits = validUnitsFor(type);
  // A container with a time unit is a fixed-time cap (AMRAP / "for time"), not
  // a loop count: its children run as a checklist for the whole duration and
  // are never repeated N times. That difference is invisible in a bare
  // number + unit pair, so the dialog spells it out (docs/schema.md).
  const isTimeCappedContainer = !isLeaf && (unit === "s" || unit === "m");

  // `hasChildren` from the plan view means "renders as a container", which is
  // also true of an EMPTY Group — turning one of those back into a step loses
  // nothing, so the type guard keys off actual children instead.
  //
  // A node that DOES hold steps is offered block types only. The compiler
  // recurses into any node with children but only multiplies rounds when the
  // type is Group (workoutCompiler.js), so retyping a 3-round block to
  // "Strength" silently ran its contents once and threw the rounds away.
  const holdsChildren =
    Array.isArray(node?.children) && node.children.length > 0;
  const typeGroups = useMemo(
    () => (holdsChildren ? TYPE_GROUPS.filter((g) => g.holdsChildren) : TYPE_GROUPS),
    [holdsChildren],
  );

  // Field relevance (issue #69). Every optional field in the schema is editable,
  // but only where it can do something — a recovery phase has no working load,
  // and a rep-based phase always waits for the user whatever `autoStart` says
  // (translatePlan forces auto_start:false for every non-time unit). A field
  // hidden because it cannot apply is cleared on save, not left behind.
  const isRecovery = RECOVERY_TYPES.includes(type);
  const supportsLoad = isLeaf && !isRecovery;
  const supportsAutoStart = isLeaf && isTimedUnit(unit);
  // Linked to previous (#177): a counted step finished with the one before it
  // on one Done. Needs a step before it, so not on a group's first step.
  const lastIndex = Array.isArray(indexPath) ? indexPath[indexPath.length - 1] : null;
  const supportsLink =
    isLeaf && !isRecovery && isCountedUnit(unit) && !(Number.isInteger(lastIndex) && lastIndex === 0);
  // seedSetCells yields cells only for a node that actually repeats, so an
  // empty row IS "this doesn't run in sets".
  const sets = valueCells.length;
  const supportsPerSet = supportsLoad && sets > 0;
  const loadDim = loadDimFor(loadUnit);
  const loadUnitLabel = LOAD_UNITS.find((u) => u.value === loadUnit)?.label || "";

  // With the per-set grid open, set 1's cell IS the phase's figure — it is what
  // gets written to the static field on save. Mirroring it into these inputs
  // read-only avoids two controls over one number, where whichever the user
  // didn't touch quietly loses. The unit pickers beside them stay live.
  const perSetActive = supportsPerSet && perSet;
  const valueField = perSetActive ? (valueCells[0] ?? "") : value;
  const loadField = perSetActive ? (loadCells[0] ?? "") : load;

  const repEstimate = useRepEstimate(
    isLeaf && isCountedUnit(unit) && Number(value) > 0
      ? { ...node, value: Number(value), unit }
      : null,
  );
  const repBudgetHint =
    repEstimate && repEstimate.totalSec >= 5
      ? `~${Math.round(repEstimate.totalSec)}s estimated`
      : null;

  const handleTypeChange = useCallback(
    (nextType) => {
      const valid = validUnitsFor(nextType);
      // Crossing the leaf/container boundary re-seeds the unit even when the
      // old one is still in range: `s` is a duration on a Work but a whole-
      // block time cap on a Group, so carrying it across would silently turn
      // a 30-second exercise into a 30-second AMRAP.
      const crossed =
        CONTAINER_TYPES.includes(nextType) !== CONTAINER_TYPES.includes(type);
      setType(nextType);
      setUnit((curr) => (!crossed && valid.includes(curr) ? curr : valid[0]));
    },
    [type],
  );

  // Switching kg ⇄ % 1RM re-seeds from whatever the node stored for the new
  // unit, so kilograms are never carried across as percentages.
  const handleLoadUnitChange = useCallback(
    (nextUnit) => {
      setLoadUnit(nextUnit);
      const dim = loadDimFor(nextUnit);
      const stored = Number(node?.[dim]);
      const staticLoad = Number.isFinite(stored) && stored > 0 ? stored : null;
      setLoad(staticLoad === null ? "" : String(staticLoad));
      setLoadCells((cells) =>
        seedSetCells(node?.setScheme?.[dim], staticLoad, cells.length),
      );
    },
    [node],
  );

  const handleValueCell = useCallback((idx, next) => {
    setValueCells((cells) => cells.map((cell, i) => (i === idx ? next : cell)));
  }, []);

  const handleLoadCell = useCallback((idx, next) => {
    setLoadCells((cells) => cells.map((cell, i) => (i === idx ? next : cell)));
  }, []);

  const handleSave = useCallback(() => {
    const numValue = value === "" ? undefined : Number(value);
    const finalNode = {
      label,
      type,
      value: Number.isFinite(numValue) ? numValue : value || undefined,
      unit,
      description: description || undefined,
    };

    // Load, effort and auto-start are written where they apply and cleared
    // where they cannot: a Work turned into a Rest should not keep a working
    // load, and only one of weight / intensityPct may ever be stored.
    const loadNumber = supportsLoad ? Number(load) : NaN;
    const hasLoad = Number.isFinite(loadNumber) && loadNumber > 0;
    finalNode.weight =
      hasLoad && loadDim === "weight" ? loadNumber : undefined;
    finalNode.intensityPct =
      hasLoad && loadDim === "intensityPct" ? loadNumber : undefined;
    finalNode.intensityTarget = supportsLoad
      ? intensityTarget.trim() || undefined
      : undefined;
    finalNode.autoStart = supportsAutoStart ? autoStart : undefined;
    finalNode.linkToPrevious = supportsLink && linkToPrevious ? true : undefined;

    // Per-set variation (issue #69). "Same each set" clears the scheme
    // outright; "Per set" writes back under the same contract the generator is
    // held to — only genuinely varying dimensions are arrayed, and the static
    // field always mirrors set 1 (docs/schema.md).
    let scheme;
    if (supportsPerSet && perSet) {
      const resolvedValue = resolveSetCells(valueCells, finalNode.value);
      if (resolvedValue.value !== null) finalNode.value = resolvedValue.value;
      const resolvedLoad = resolveSetCells(
        loadCells,
        hasLoad ? loadNumber : null,
      );
      if (resolvedLoad.value !== null) finalNode[loadDim] = resolvedLoad.value;
      scheme = {};
      if (resolvedValue.array) scheme.value = resolvedValue.array;
      if (resolvedLoad.array) scheme[loadDim] = resolvedLoad.array;
      if (Object.keys(scheme).length === 0) scheme = undefined;
    }
    finalNode.setScheme = scheme;

    // Creating a Group/Circuit starts it as an empty container. Only on
    // create — an edit-mode Object.assign with children would wipe the
    // existing subtree.
    if (isCreate && CONTAINER_TYPES.includes(type)) {
      finalNode.children = [];
    }
    if (isCreate) {
      if (!onCreate) return;
      onCreate(finalNode);
    } else {
      if (!onSave) return;
      onSave(indexPath, finalNode);
    }
    onOpenChange(false);
  }, [
    isCreate,
    onCreate,
    onSave,
    indexPath,
    label,
    type,
    value,
    unit,
    description,
    load,
    loadDim,
    intensityTarget,
    autoStart,
    linkToPrevious,
    supportsLoad,
    supportsAutoStart,
    supportsLink,
    supportsPerSet,
    perSet,
    valueCells,
    loadCells,
    onOpenChange,
  ]);

  const handleDelete = useCallback(() => {
    if (onDelete) onDelete(indexPath);
    onOpenChange(false);
  }, [onDelete, indexPath, onOpenChange]);

  const handleDuplicate = useCallback(() => {
    if (onDuplicate) onDuplicate(indexPath);
    onOpenChange(false);
  }, [onDuplicate, indexPath, onOpenChange]);

  const exerciseNameForAi = label || node?.label || "Exercise";

  const handleGenerateAi = useCallback(async () => {
    setAiLoading(true);
    setAiError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await llmApi.enhanceExercise(
        exerciseNameForAi,
        {
          type,
          fitnessLevel: userProfile?.fitnessLevel,
        },
        llmSettings,
        controller.signal,
      );
      const formatted = formatAiResponse(res);
      if (formatted) setDescription(formatted);
    } catch (err) {
      if (err.name !== "AbortError") {
        setAiError(err.message || "Failed to generate AI notes.");
      }
    } finally {
      setAiLoading(false);
      abortRef.current = null;
    }
  }, [exerciseNameForAi, type, userProfile, llmSettings]);

  if (!node) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // minmax(0,1fr): the one column may shrink to the dialog, or its
        // widest row (the per-set toggle) widens every row past a phone's
        // edge.
        // z-[70]: the plan editor also sits inside bottom sheets (z-[60]),
        // such as the checklist and Write manually (#173).
        className="z-[70] w-[calc(100vw-2rem)] max-w-md mx-auto rounded-lg max-h-[85vh] overflow-y-auto grid-cols-[minmax(0,1fr)]"
        overlayClassName="z-[70]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-2">
          <DialogDescription className="sr-only">
            Edit exercise details including type, value, unit, and notes.
          </DialogDescription>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium uppercase tracking-wider cursor-pointer hover:opacity-80 transition-opacity",
                    getBadgeClass(type, label),
                  )}
                >
                  {type}
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="z-[70]">
                {typeGroups.map((group, groupIdx) => (
                  <React.Fragment key={group.label}>
                    {groupIdx > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                      {group.label}
                      {group.holdsChildren && " · hold steps, run for rounds"}
                    </DropdownMenuLabel>
                    {group.types.map((t) => (
                      <DropdownMenuItem
                        key={t}
                        onSelect={() => handleTypeChange(t)}
                        className="flex items-center gap-2"
                      >
                        <span
                          className={cn(
                            "w-2 h-2 rounded-full",
                            getDotClass(t, ""),
                          )}
                        />
                        {t}
                      </DropdownMenuItem>
                    ))}
                  </React.Fragment>
                ))}
                {holdsChildren && (
                  <p className="px-2 py-1.5 text-[11px] text-muted-foreground max-w-[15rem]">
                    Step types aren't offered: this block holds steps, and only
                    a block can run them for rounds.
                  </p>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {repBudgetHint && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
                <Clock className="w-3 h-3" />
                {repBudgetHint}
              </span>
            )}
          </div>

          <DialogTitle asChild>
            <Input
              ref={titleInputRef}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="New exercise"
              className="border-0 pl-3 pr-0 h-auto py-1 text-xl md:text-2xl font-semibold bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* The unit, the value and the load: the shared amount fields, set
              the same way here as in Free training's log (#164). */}
          <StepAmountFields
            units={validUnits}
            unit={unit}
            onUnit={setUnit}
            value={valueField === "" ? startValueFor(unit) : Number(valueField)}
            onValue={(v) => setValue(v)}
            load={supportsLoad ? Number(loadField) || 0 : undefined}
            onLoad={supportsLoad ? (v) => setLoad(v > 0 ? v : "") : undefined}
            loadUnit={loadUnit}
            onLoadUnit={supportsLoad ? handleLoadUnitChange : undefined}
            name={label}
            disabled={perSetActive}
            disabledHint={perSetActive ? "Set per set below." : undefined}
          />

          {isTimeCappedContainer && (
            <p className="text-xs text-muted-foreground -mt-2">
              Fixed-time block (AMRAP / for time): everything inside runs as a
              checklist for the whole duration, not repeated in rounds.
            </p>
          )}

          {supportsLoad && (
            <div className="space-y-1.5">
              <Label htmlFor="node-effort" className={FIELD_LABEL_CLASS}>
                Effort / target
              </Label>
              <Input
                id="node-effort"
                value={intensityTarget}
                onChange={(e) => setIntensityTarget(e.target.value)}
                placeholder="RPE 8, 75% 1RM, 2k pace"
                className="h-11 text-base"
              />
            </div>
          )}

          {supportsAutoStart && (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Label htmlFor="node-auto-start" className={FIELD_LABEL_CLASS}>
                  Auto-start
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Run straight into this phase instead of waiting for you.
                </p>
              </div>
              <Switch
                id="node-auto-start"
                checked={autoStart}
                onCheckedChange={setAutoStart}
              />
            </div>
          )}

          {supportsLink && (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Label htmlFor="node-link-previous" className={FIELD_LABEL_CLASS}>
                  Linked to previous
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Do it straight after the step before, with one Done for both.
                </p>
              </div>
              <Switch
                id="node-link-previous"
                checked={linkToPrevious}
                onCheckedChange={setLinkToPrevious}
              />
            </div>
          )}

          {supportsPerSet && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className={FIELD_LABEL_CLASS}>{sets} sets</Label>
                <SegmentedToggle
                  value={perSet ? "vary" : "same"}
                  onChange={(next) => setPerSet(next === "vary")}
                  options={[
                    { value: "same", label: "Same each set" },
                    { value: "vary", label: "Per set" },
                  ]}
                />
              </div>
              {perSet && (
                <div className="space-y-2 rounded-md border border-border/50 p-2">
                  <SetCellRow
                    label={UNIT_LABELS[unit] || unit}
                    cells={valueCells}
                    onChange={handleValueCell}
                  />
                  <SetCellRow
                    label={loadUnitLabel}
                    cells={loadCells}
                    onChange={handleLoadCell}
                  />
                </div>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <div className="relative">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Form cues, intent, AI-generated tips and alternatives — all in one place."
                className="text-sm min-h-[180px] font-normal pr-28"
              />
              {isLeaf && llmSettings && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleGenerateAi}
                  disabled={aiLoading}
                  title={description ? "Regenerate notes with AI" : "Generate notes with AI"}
                  aria-label={description ? "Regenerate notes with AI" : "Generate notes with AI"}
                  className="absolute top-2 right-2 h-7 w-7 bg-background/80 backdrop-blur"
                >
                  {aiLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                </Button>
              )}
            </div>
            {aiError && (
              <p className="text-xs text-destructive">{aiError}</p>
            )}
          </div>
        </div>

        {/* Wraps on a narrow phone: Delete, Duplicate, Cancel and Save don't
            fit one 390 px row. */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-border/50">
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={!onDelete}
              className="h-8 px-2 text-muted-foreground hover:text-destructive disabled:opacity-30"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Delete
            </Button>
            {!isCreate && onDuplicate && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleDuplicate}
                className="h-8 px-2 text-muted-foreground hover:text-foreground"
              >
                <Copy className="h-3.5 w-3.5 mr-1" />
                Duplicate
              </Button>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 px-3"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              className="h-8 px-3"
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExerciseDetailDialog;
